import React from "react";
import styles from "./CustomButton.module.css";

/**
 * CustomButton component with multiple variants
 * @param {Object} style - Custom inline styles
 * @param {React.ReactNode} children - Button content
 * @param {Function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 * @param {boolean} rounded - Apply rounded pill style
 * @param {string} pre - Text to display before children
 * @param {React.ReactNode} icon - Icon component
 * @param {string} type - Button type (submit, button, reset)
 * @param {string} variant - Button variant (outline, gray-outline, orange-outline, danger, customColor) or use CSS module class directly
 * @param {boolean} disabled - Disable the button
 */

const CustomButton = ({
  style,
  children,
  onClick,
  className = "",
  rounded,
  pre,
  icon,
  type = "button",
  variant = "default", // default to primary button
  disabled,
}) => {
  // Map variant strings to CSS module classes
  const variantClasses = {
    default: styles.customButton,
    outline: styles.themeOutlineButton,
    "gray-outline": styles.grayOutlineButton,
    "orange-outline": styles.orangeOutlineButton,
    danger: styles.dangerButton,
    customColor: styles.customColorButton,
  };

  // Determine the button class to use - allow passing direct CSS module references
  const buttonClass = variantClasses[variant] || variant;

  // Combine all classes
  const combinedClasses = [
    buttonClass, // Always include the variant class
    disabled ? styles.disabledButton : "", // Add disabled class if needed
    rounded ? "rounded-pill" : "",
    "text-nowrap p4 fw-semibold d-flex align-items-center justify-content-center gap-1",
    className
  ].filter(Boolean) //  this removes "", undefined, null, false values from the array
  .join(" ");

  return (
    <button
      type={type}
      onClick={onClick}
      style={style}
      disabled={disabled}
      className={combinedClasses}
    >
      {icon}
      {pre && <span>{pre}</span>}
      {children}
    </button>
  );
};

export default CustomButton;